import React, {useEffect, useState} from 'react';
import {ActivityIndicator, Linking, Platform, StyleSheet, Text, TouchableOpacity, View} from 'react-native';
import networkService from '../Network/NetworkManager';
import {loadCreditsOverview} from '../Services/CreditsService';
import {useLocale} from '../Localization/LocaleContext';

const TOP_UP_URL = '/workspace/plans?view=plans&package=ala-carte';

const formatCredits = (value, locale) =>
  new Intl.NumberFormat(locale === 'fr' ? 'fr-FR' : locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(value);

const creditColor = value => {
  if (value <= 0) return '#f3a4a8';
  if (value <= 2) return '#f0c674';
  return '#9ee8c9';
};

const openTopUp = async () => {
  if (Platform.OS === 'web') {
    window.location.assign(TOP_UP_URL);
    return;
  }

  const absoluteUrl = `https://tradly.app${TOP_UP_URL}`;
  const supported = await Linking.canOpenURL(absoluteUrl);
  if (supported) await Linking.openURL(absoluteUrl);
};

const loadBalance = async () => {
  const authKey = networkService.getAuthKey();
  if (!authKey) return null;
  const overview = await loadCreditsOverview();
  return overview.balance?.available ?? null;
};

const AICreditsControl = () => {
  const {translate: t, locale} = useLocale();
  const [balance, setBalance] = useState(undefined);
  const [showTopUp, setShowTopUp] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const value = await loadBalance();
        if (!cancelled) setBalance(value);
      } catch {
        if (!cancelled) setBalance(null);
      }
    };

    refresh();
    const interval = setInterval(refresh, 120000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  if (balance === null && !networkService.getAuthKey()) return null;

  return (
    <View
      style={styles.container}
      accessibilityLabel={t('common.ai_credits_and_top_up', 'AI credits and top up')}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}>
      <TouchableOpacity
        onPress={() => setShowTopUp(value => !value)}
        style={styles.badge}
        accessibilityRole="button"
        accessibilityLabel={
          balance === undefined
            ? t('common.ai_credits_loading', 'Loading AI credits')
            : balance === null
            ? t('common.ai_credits_unavailable', 'AI credits unavailable')
            : t('common.ai_credits_available', {count: formatCredits(balance, locale)}, 'Available AI credits: {count}')
        }
        title={
          balance === undefined
            ? t('common.ai_credits_loading', 'Loading AI credits')
            : balance === null
            ? t('common.ai_credits_unavailable_top_up', 'AI credits unavailable — click to top up')
            : t('common.ai_credits_available_top_up', {count: formatCredits(balance, locale)}, 'Available AI credits: {count} — click to add credits')
        }>
        {balance === undefined ? (
          <ActivityIndicator size="small" color="#9ee8c9" />
        ) : (
          <Text style={[styles.iconGlyph, {color: balance === null ? '#f3a4a8' : creditColor(balance)}]}>✦</Text>
        )}
      </TouchableOpacity>
      {isHovered && balance !== undefined && (
        <View pointerEvents="none" style={styles.tooltip}>
          <Text style={styles.tooltipText}>
            {balance === null ? t('common.ai_credits_unavailable', 'AI credits unavailable') : t('common.ai_credits_available', {count: formatCredits(balance, locale)}, 'Available AI credits: {count}')}
          </Text>
        </View>
      )}
      {showTopUp && (
        <TouchableOpacity onPress={openTopUp} style={styles.topUp} accessibilityRole="link">
          <Text style={[styles.topUpText, {color: 'var(--color-text)'}]}>{t('common.ai_credits_add', 'Add credits')}</Text>
        </TouchableOpacity>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 4,
    position: 'relative',
  },
  badge: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#4f9f85',
    backgroundColor: '#1e3b31',
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconGlyph: {
    fontSize: 18,
    lineHeight: 20,
    fontWeight: '700',
  },
  tooltip: {
    position: 'absolute',
    top: 38,
    right: 0,
    zIndex: 100,
    minWidth: 150,
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 6,
    backgroundColor: '#111412',
    borderWidth: 1,
    borderColor: '#4a4d4b',
  },
  tooltipText: {
    color: '#f7f7f7',
    fontSize: 11,
    whiteSpace: 'nowrap',
  },
  topUp: {
    marginLeft: 6,
    minHeight: 26,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#4f9f85',
    justifyContent: 'center',
  },
  topUpText: {
    fontSize: 11,
    fontWeight: '700',
  },
});

export default AICreditsControl;
